import { useState } from 'react';
import DistrictDetail from './DistrictDetail';
import { isEnabled } from '../../lib/featureFlags';
import { trackGrowthOnce } from '../../lib/growth';

export default function RaceCard({ district }) {
  const [showDetail, setShowDetail] = useState(false);

  // Defensive checks for missing data
  if (!district) return null;

  const useWinProbability = isEnabled('WIN_PROBABILITY');
  const useGradientColors = isEnabled('GRADIENT_COLORS');

  const getRatingBadge = (rating) => {
    const classes = {
      'Safe D': 'badge-safe-d',
      'Likely D': 'badge-likely-d',
      'Lean D': 'badge-lean-d',
      'Toss-Up': 'badge-toss-up',
      'Lean R': 'badge-lean-r',
      'Likely R': 'badge-likely-r',
      'Safe R': 'badge-safe-r'
    };
    return classes[rating] || 'badge-toss-up';
  };

  // Get card style based on rating/party
  const getCardStyle = () => {
    const rating = district.model_rating || '';
    const isHot = useWinProbability && district.competitivenessScore >= 70;

    if (isHot) return 'card-hot';
    if (rating.includes('D')) return 'card-dem';
    if (rating.includes('R')) return 'card-rep';
    if (rating === 'Toss-Up') return 'card-tossup';
    return '';
  };

  const formatMargin = (margin) => {
    if (!margin && margin !== 0) return 'N/A';
    const prefix = margin > 0 ? 'D+' : 'R+';
    return `${prefix}${Math.abs(margin).toFixed(1)}`;
  };

  const getPartyIcon = (party) => {
    if (!party) return null;
    if (party.toLowerCase().includes('democrat')) {
      return '/images/democrat.jpg';
    }
    if (party.toLowerCase().includes('republican')) {
      return '/images/republican.jpg';
    }
    return null;
  };

  return (
    <>
      <button
        onClick={() => {
          trackGrowthOnce('district_explored', 'first-district', {
            district: district.district_code || 'unknown',
            placement: 'race_card'
          });
          setShowDetail(true);
        }}
        className={`w-full text-left glass rounded-xl p-4 sm:p-5 card-hover group ${getCardStyle()}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3 sm:mb-4 gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-3">
              <h3 className="text-lg sm:text-xl font-bold text-white group-hover:text-accent-blue transition-colors">
                {district.district_code || 'Unknown'}
              </h3>
              {district.is_open_seat && (
                <span className="px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs rounded badge-open whitespace-nowrap">
                  OPEN
                </span>
              )}
              {useWinProbability && district.competitivenessScore >= 70 && (
                <span className="px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs rounded badge-hot whitespace-nowrap"
                      title={`Competitiveness: ${district.competitivenessScore}/100`}>
                  🔥 HOT
                </span>
              )}
            </div>
            <div className="text-xs sm:text-sm text-slate-400 mt-1 truncate">{district.name || 'District name not available'}</div>
          </div>

          {useWinProbability && district.winDisplay ? (
            <div className="text-right flex-shrink-0">
              <div
                className="text-2xl sm:text-3xl font-bold transition-colors"
                style={{ color: useGradientColors ? district.gradientColor : undefined }}
              >
                {district.winDisplay.party} {district.winDisplay.percentage}%
              </div>
              <div className="text-[10px] sm:text-xs text-slate-400">
                {district.confidenceLabel} conf.
              </div>
            </div>
          ) : (
            <span className={`px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold flex-shrink-0 ${getRatingBadge(district.model_rating)}`}>
              {district.model_rating || 'N/A'}
            </span>
          )}
        </div>

        {/* Probability Bar (only show with win probability feature) */}
        {useWinProbability && district.winProbD !== undefined && (
          <div className="mb-4" role="progressbar"
               aria-label={`${district.winDisplay?.margin || 'Unknown'} win probability: ${district.winDisplay?.percentage || 0}%`}
               aria-valuenow={district.winDisplay?.percentage || 0}
               aria-valuemin={0}
               aria-valuemax={100}>
            <div
              className="relative h-2 rounded-full overflow-hidden"
              style={{
                background: useGradientColors
                  ? 'linear-gradient(90deg, oklch(45% 0.20 25) 0%, oklch(60% 0.22 300) 50%, oklch(45% 0.20 250) 100%)'
                  : 'linear-gradient(90deg, rgb(185,28,28) 0%, rgb(147,51,234) 50%, rgb(30,64,175) 100%)'
              }}
            >
              {/* Marker at exact probability */}
              <div
                className="absolute top-0 bottom-0 w-1 bg-white shadow-lg"
                style={{ left: `${district.winProbD * 100}%`, transform: 'translateX(-50%)' }}
              />
            </div>
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>R</span>
              <span>Toss-up</span>
              <span>D</span>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className={`grid ${useWinProbability ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2'} gap-3 sm:gap-4 mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-slate-700/50`}>
          <div>
            <div className="text-[10px] sm:text-xs text-slate-500">2024 Margin</div>
            <div className="text-base sm:text-lg font-semibold text-white mt-0.5">
              {formatMargin(district.margin_2024)}
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-[10px] sm:text-xs text-slate-500">Incumbent</div>
            <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5">
              {district.incumbent_party && getPartyIcon(district.incumbent_party) && (
                <img
                  src={getPartyIcon(district.incumbent_party)}
                  alt={district.incumbent_party}
                  className="w-5 h-5 sm:w-6 sm:h-6 rounded object-cover flex-shrink-0"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <div className="text-base sm:text-lg font-semibold text-white truncate">
                {district.incumbent_name || 'None'}
              </div>
            </div>
          </div>
          {useWinProbability && district.competitivenessScore !== undefined && (
            <div className="col-span-2 sm:col-span-1">
              <div className="text-[10px] sm:text-xs text-slate-500">Competitiveness</div>
              <div className="text-base sm:text-lg font-semibold text-white mt-0.5">
                {district.competitivenessScore}/100
              </div>
            </div>
          )}
        </div>

        {/* Confidence indicator (only show if NOT using win probability) */}
        {!useWinProbability && district.model_confidence && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
              <span>Confidence</span>
              <span>{(district.model_confidence * 100).toFixed(0)}%</span>
            </div>
            <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full"
                style={{ width: `${district.model_confidence * 100}%` }}
              />
            </div>
          </div>
        )}
      </button>

      {showDetail && (
        <DistrictDetail
          district={district}
          onClose={() => setShowDetail(false)}
        />
      )}
    </>
  );
}
